import React, { useMemo } from 'react';
import { useBlockSubjectInputGroupsValidation } from './logic/useBlockSubjectInputGroupsValidation';
import { AlertNote, ErrorNote, InfoNote } from 'venn-ui-kit';
import { PanelItem } from '../components/shared';
import { useBlockSubjectValidation } from './logic/useBlockSubjectValidation';
import type { ValidationError } from './types';
import { assertExhaustive, useHasFF, withSuspense } from 'venn-utils';
import { useBlockValidation } from './logic/useBlockValidation';

const validationPriority = (validation: ValidationError) => {
  switch (validation.type) {
    case 'ERROR':
      return 1;
    case 'WARNING':
      return 2;
    case 'INFO':
      return 3;
    default:
      throw assertExhaustive(validation.type);
  }
};

const ValidationMessage = ({ validation }: { validation: ValidationError }) => {
  switch (validation.type) {
    case 'ERROR':
      return <ErrorNote size="small">{validation.message}</ErrorNote>;
    case 'WARNING':
      return <AlertNote size="small">{validation.message}</AlertNote>;
    case 'INFO':
      return <InfoNote size="small">{validation.message}</InfoNote>;
    default:
      throw assertExhaustive(validation.type);
  }
};

export const MessagesSection = withSuspense(
  null,
  function MessageSectionInner({ selectedBlockId }: { selectedBlockId: string }) {
    const hasBlockAssignment = useHasFF('block_assignment_ff');
    const inputGroupMessages = useBlockSubjectInputGroupsValidation(selectedBlockId);
    const subjectMessages = useBlockSubjectValidation(selectedBlockId);
    const blockMessages = useBlockValidation(selectedBlockId);

    const messages = useMemo(() => {
      const allErrors = [...blockMessages, ...inputGroupMessages, ...subjectMessages];
      const containsErrors = allErrors.some((error) => error.type === 'ERROR');
      const messagesToDisplay = allErrors.filter((error) => (containsErrors ? error.type === 'ERROR' : true));
      messagesToDisplay.sort((a, b) => validationPriority(a) - validationPriority(b));
      return messagesToDisplay;
    }, [inputGroupMessages, subjectMessages, blockMessages]);

    if (!hasBlockAssignment) {
      return null;
    }

    return messages.map((message) => (
      <PanelItem key={message.message}>
        <ValidationMessage validation={message} />
      </PanelItem>
    ));
  },
);
